import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Users, Calendar, ClipboardList, Bell, Clock, CheckCircle, 
  XCircle, BookOpen, GraduationCap, UserCheck, MessageCircle
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeacherDashboard = () => {
  const { getAuthHeader, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [notices, setNotices] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendanceData, setAttendanceData] = useState({});

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [
        teachersRes,
        studentsRes,
        classesRes,
        timetableRes,
        leavesRes,
        noticesRes
      ] = await Promise.all([
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/timetable/entries`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/hr/leaves/my`, { headers: getAuthHeader() }).catch(() => ({ data: { leaves: [] } })),
        axios.get(`${API_URL}/api/notices`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);

      // Handle paginated or array response for teachers
      const teachersData = teachersRes.data.items || teachersRes.data || [];
      const currentTeacher = teachersData.find(t => t.email === user?.email) || teachersData[0];
      setTeacherInfo(currentTeacher);

      // Handle paginated response for students
      const studentsData = studentsRes.data.items || studentsRes.data || [];
      setStudents(Array.isArray(studentsData) ? studentsData : []);
      
      // Handle paginated response for classes
      const classesData = classesRes.data.items || classesRes.data || [];
      setClasses(Array.isArray(classesData) ? classesData : []);
      
      // Handle timetable - filter by teacher_id
      const timetableData = timetableRes.data.items || timetableRes.data || [];
      // Filter timetable for current teacher using teacher_id from teachers collection
      const myTimetable = currentTeacher ? timetableData.filter(t => t.teacher_id === currentTeacher.id) : [];
      setTimetable(myTimetable);
      
      // Handle leaves - from HR endpoint with proper response structure
      const leavesData = leavesRes.data?.leaves || leavesRes.data?.items || leavesRes.data || [];
      setLeaveRequests(Array.isArray(leavesData) ? leavesData : []);
      
      // Handle notices - with fallback for errors
      const noticesData = noticesRes.data?.items || noticesRes.data || [];
      setNotices(Array.isArray(noticesData) ? noticesData.slice(0, 5) : []);

      // Set selected class - prefer teacher's assigned class or class_teacher_of
      if (currentTeacher?.class_teacher_of) {
        setSelectedClass(currentTeacher.class_teacher_of);
      } else if (myTimetable.length > 0) {
        setSelectedClass(myTimetable[0].class_id);
      } else if (classesData.length > 0) {
        setSelectedClass(classesData[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Silently handle errors - some APIs may not be available
    } finally {
      setLoading(false);
    }
  };

  // Get today's classes
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
  const todayClasses = timetable.filter(t => t.day?.toLowerCase() === today);

  // Get students by class
  const classStudents = students.filter(s => s.class_id === selectedClass);

  // Calculate stats
  const myClasses = [...new Set(timetable.map(t => t.class_id))];
  const myStudentsCount = students.filter(s => myClasses.includes(s.class_id)).length;

  const statCards = [
    { 
      title: 'My Students', 
      value: myStudentsCount, 
      icon: Users, 
      color: 'bg-blue-500',
      subtext: `${myClasses.length} classes`
    },
    { 
      title: "Today's Classes", 
      value: todayClasses.length, 
      icon: Calendar, 
      color: 'bg-green-500',
      subtext: today.charAt(0).toUpperCase() + today.slice(1)
    },
    { 
      title: 'My Leaves', 
      value: leaveRequests.filter(l => l.status === 'approved').length, 
      icon: ClipboardList, 
      color: 'bg-amber-500',
      subtext: `${leaveRequests.filter(l => l.status === 'pending').length} pending`
    },
    { 
      title: 'Total Classes/Week', 
      value: timetable.length, 
      icon: BookOpen, 
      color: 'bg-purple-500',
      subtext: 'Weekly schedule'
    }
  ];

  const markAttendance = async (studentId, status) => {
    try {
      await axios.post(`${API_URL}/api/attendance/student`, {
        student_id: studentId,
        date: attendanceDate,
        status: status,
        attendance_type: 'daily'
      }, { headers: getAuthHeader() });
      
      setAttendanceData(prev => ({ ...prev, [studentId]: status }));
      toast.success('Attendance marked');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark attendance');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Teacher Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Teacher Dashboard">
      <div className="space-y-6">
        {/* Welcome Banner */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Welcome, {teacherInfo?.name || user?.name || 'Teacher'}!</h2>
                  <p className="text-emerald-100 mt-1">
                    Employee ID: {teacherInfo?.employee_id} | {teacherInfo?.subject_specialization?.join(', ') || 'Teaching Staff'}
                  </p>
                </div>
                <GraduationCap className="h-16 w-16 text-emerald-200 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card data-testid={`stat-${stat.title.toLowerCase().replace(/[' ]/g, '-')}`}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                    <div className={`${stat.color} p-2 rounded-lg`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stat.value}</div>
                    <p className="text-xs text-muted-foreground mt-1">{stat.subtext}</p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Schedule & Attendance */}
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="schedule">
              <TabsList>
                <TabsTrigger value="schedule">Today's Schedule</TabsTrigger>
                <TabsTrigger value="attendance">Mark Attendance</TabsTrigger>
                <TabsTrigger value="students">My Students</TabsTrigger>
              </TabsList>

              <TabsContent value="schedule">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Today's Classes
                    </CardTitle>
                    <CardDescription>
                      {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {todayClasses.length > 0 ? (
                      <div className="space-y-3">
                        {todayClasses.sort((a, b) => a.time_slot?.localeCompare(b.time_slot)).map((cls, i) => {
                          const classInfo = classes.find(c => c.id === cls.class_id);
                          return (
                            <div key={i} className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                              <div className="w-24 text-sm font-mono bg-muted px-2 py-1 rounded">
                                {cls.time_slot}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium">{cls.subject}</p>
                                <p className="text-sm text-muted-foreground">
                                  {classInfo?.name || 'Class'}
                                </p>
                              </div>
                              <Button variant="outline" size="sm" onClick={() => {
                                setSelectedClass(cls.class_id);
                                document.querySelector('[value="attendance"]')?.click();
                              }}>
                                <UserCheck className="h-4 w-4 mr-1" />
                                Take Attendance
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Calendar className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                        <p className="text-muted-foreground">No classes scheduled for today</p>
                      </div>
                    )}

                    {/* Weekly Overview */}
                    <div className="mt-6">
                      <h4 className="text-sm font-medium mb-3">Weekly Schedule Overview</h4>
                      <div className="grid grid-cols-6 gap-2">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => {
                          const dayClasses = timetable.filter(t => 
                            t.day?.toLowerCase().startsWith(day.toLowerCase())
                          );
                          return (
                            <div key={day} className="text-center p-2 bg-muted rounded-lg">
                              <p className="text-xs font-medium">{day}</p>
                              <p className="text-lg font-bold">{dayClasses.length}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="attendance">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Mark Attendance</CardTitle>
                        <CardDescription>Select class and mark student attendance</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Select value={selectedClass} onValueChange={setSelectedClass}>
                          <SelectTrigger className="w-40">
                            <SelectValue placeholder="Select class" />
                          </SelectTrigger>
                          <SelectContent>
                            {classes.map(cls => (
                              <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <input
                          type="date"
                          value={attendanceDate}
                          onChange={(e) => setAttendanceDate(e.target.value)}
                          className="px-3 py-2 border rounded-md text-sm"
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {classStudents.length > 0 ? (
                      <div className="space-y-2">
                        {classStudents.map(student => (
                          <div key={student.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-xs font-medium">
                                  {student.name?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium">{student.name}</p>
                                <p className="text-xs text-muted-foreground">{student.student_id || student.roll_number}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={attendanceData[student.id] === 'present' ? 'default' : 'outline'}
                                className={attendanceData[student.id] === 'present' ? 'bg-green-600 hover:bg-green-700' : ''}
                                onClick={() => markAttendance(student.id, 'present')}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant={attendanceData[student.id] === 'absent' ? 'default' : 'outline'}
                                className={attendanceData[student.id] === 'absent' ? 'bg-red-600 hover:bg-red-700' : ''}
                                onClick={() => markAttendance(student.id, 'absent')}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">No students in selected class</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="students">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>My Students</CardTitle>
                        <CardDescription>Students in your classes</CardDescription>
                      </div>
                      <Select value={selectedClass} onValueChange={setSelectedClass}>
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent>
                          {classes.map(cls => (
                            <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {classStudents.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {classStudents.map(student => (
                          <div key={student.id} className="flex items-center gap-3 p-3 border rounded-lg">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-sm font-medium">
                                {student.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{student.name}</p>
                              <p className="text-xs text-muted-foreground">{student.student_id}</p>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => window.location.href = '/communication'}>
                              <MessageCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-8">No students in selected class</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Quick Actions & Notices */}
          <div className="space-y-6">
            {/* My Leaves */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="h-5 w-5" />
                  My Leave Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                {leaveRequests.length > 0 ? (
                  <div className="space-y-3">
                    {leaveRequests.slice(0, 5).map(leave => (
                      <div key={leave.id} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{leave.leave_type?.toUpperCase()} Leave</p>
                            <p className="text-sm text-muted-foreground">{leave.start_date} to {leave.end_date}</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            leave.status === 'approved' ? 'bg-green-100 text-green-700' :
                            leave.status === 'rejected' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {leave.status?.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm mt-1">{leave.reason}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No leave requests</p>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/attendance'}>
                  <UserCheck className="h-4 w-4 mr-2" />
                  View All Attendance
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/exams'}>
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Manage Exams
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/timetable'}>
                  <Calendar className="h-4 w-4 mr-2" />
                  View Timetable
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => window.location.href = '/communication'}>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Messages
                </Button>
              </CardContent>
            </Card>

            {/* Notices */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="h-5 w-5" />
                  Recent Notices
                </CardTitle>
              </CardHeader>
              <CardContent>
                {notices.length > 0 ? (
                  <div className="space-y-3">
                    {notices.map(notice => (
                      <div key={notice.id} className="p-3 border rounded-lg">
                        <p className="font-medium text-sm">{notice.title}</p>
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{notice.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-4">No notices</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TeacherDashboard;
